import { useState } from 'react';
import { toAppError } from '@repo/api-client';
import { useActiveOrganizationId } from '@repo/api-client/react';
import { useCreateClient, useUploadClientPhoto } from '@repo/queries';
import { Navigate, useNavigate } from 'react-router-dom';
import { useToast } from '@/components/ui/use-toast';
import { ClientForm } from '@/features/client/ClientForm';
import { clientFormToWire, type ClientFormValues } from '@/features/client/clientFormSchema';
import { Page, PageHeader } from '@/features/common/PageHeader';
import { AiPill } from '@/features/ai/AiPill';
import { useAiComposer } from '@/features/ai/AiComposerContext';
import { describeAppError } from '@/features/common/describeAppError';
import {
  ClientAvatar,
  StagedImagePicker,
  inferClientTag,
} from '@/features/avatar';
import { useActiveRole } from '@/features/auth/useActiveRole';

export function ClientCreateScreen() {
  const navigate = useNavigate();
  const { canEditOrg } = useActiveRole();
  const orgId = useActiveOrganizationId();
  const { createClient, loading } = useCreateClient();
  const { uploadPhoto } = useUploadClientPhoto();
  const { toast } = useToast();
  const { open: openAi } = useAiComposer();

  // Photo is staged in component state — there's no presigned URL
  // path, and the upload endpoint is `PUT /orgs/:id/clients/:cid/photo`
  // so we don't have a `cid` to upload to until createClient resolves.
  const [photoFile, setPhotoFile] = useState<File | null>(null);
  const [stagedName, setStagedName] = useState<string>('');
  const [stagedCompany, setStagedCompany] = useState<string>('');

  async function handleSubmit(values: ClientFormValues) {
    try {
      const created = await createClient(clientFormToWire(values));
      if (photoFile && orgId) {
        try {
          await uploadPhoto({
            organizationId: orgId,
            clientId: created.id,
            file: photoFile,
          });
        } catch (err) {
          const desc = describeAppError(err);
          toast({
            variant: 'danger',
            title: 'Photo upload failed',
            description: desc.message,
          });
        }
      }
      toast({
        variant: 'success',
        title: 'Client created',
        description: `${created.name} is ready to invoice.`,
      });
      navigate(`/clients/${created.id}`, { replace: true });
    } catch (err) {
      const copy = describeAppError(toAppError(err));
      toast({
        variant: 'danger',
        title: copy.title,
        description: copy.message,
      });
    }
  }

  if (!canEditOrg) {
    return <Navigate to="/clients" replace />;
  }

  return (
    <Page>
      <PageHeader
        title="New client"
        description="Fill in the fields below."
        breadcrumbs={
          <span>
            <a href="/clients" className="hover:text-foreground">
              Clients
            </a>{' '}
            / New
          </span>
        }
      />

      <AiPill className="mb-5" onClick={() => openAi()} />

      <ClientForm
        loading={loading}
        onCancel={() => navigate(-1)}
        onSubmit={(values) => {
          setStagedName(values.name ?? '');
          setStagedCompany(values.company ?? '');
          return handleSubmit(values);
        }}
        photoSlot={
          <StagedImagePicker
            title="Photo"
            hint="Square images render best. PNG, JPG or WebP up to 5 MB."
            file={photoFile}
            onChange={setPhotoFile}
            renderPreview={(previewUrl) => (
              <ClientAvatar
                name={stagedName || 'New client'}
                photoUrl={previewUrl}
                tag={inferClientTag({
                  name: stagedName,
                  company: stagedCompany || null,
                })}
                size={64}
              />
            )}
          />
        }
      />
    </Page>
  );
}
